import React, { useEffect, useId, useRef, useState } from "react";
import { Pagination } from "rsuite";
import Button from "../../components/UI/Buttons/Button/Button";
import AccessibleDialog from "../../components/UI/Modals/AccessibleDialog/AccessibleDialog";
import {
  MISSED_CALLS_PAGE_SIZE,
  getMissedCalls,
  markMissedCallContacted,
} from "../../services/missedCallsService";
import { PageResponse } from "../../types/medicineSearch.types";
import { MissedCallResponse } from "../../types/missedCalls.types";
import {
  MISSED_CALLS_LOAD_ERROR,
  MISSED_CALLS_REFRESH_ERROR,
  mapContactError,
  shouldRefetchAfterContactError,
} from "../../utils/missedCallErrors";
import {
  formatCallerName,
  formatDateTime,
  formatWaitTime,
} from "../../utils/missedCallFormat";
import styles from "./PharmacistProfilePage.module.scss";

// ITAM-2746: the shared queue of unanswered pharmacist calls, so an adviser can
// call the patient back and record that they did.
//
// The list is GLOBAL by contract (the backend passes no consultant filter), so
// this panel takes no user id: a prop would imply a scoping the endpoint does
// not perform.
//
// Two facts are kept apart everywhere below: what the WRITE did, and whether
// the list caught up afterwards. A failed reconciliation GET never turns a
// committed write into a save failure, and never destroys the rows already on
// screen; only a FIRST load failure replaces the panel body.
//
// The queue is operational, so it also keeps itself fresh: a visible tab
// re-reads its own page on a timer, because "Nema propuštenih poziva" must
// never mean "nothing had arrived when this tab was opened".

/**
 * How often a VISIBLE tab re-reads the queue. A hidden tab polls nothing: the
 * timer is torn down on the way out and the queue is re-read on the way back
 * in, so a tab left open overnight issues no requests and is still never a
 * cycle behind when the pharmacist returns to it.
 */
const MISSED_CALLS_POLL_MS = 30000;

type Phase =
  | { phase: "loading" }
  | {
      phase: "loaded";
      page: PageResponse<MissedCallResponse>;
      refreshing: boolean;
      refreshError: string | null;
    }
  | { phase: "error" };

interface Props {
  /** Layout only, supplied by the page's panel grid. */
  className?: string;
}

const MissedCallsPanel: React.FC<Props> = ({ className }) => {
  const headingId = useId();
  const confirmTitleId = useId();
  const [state, setState] = useState<Phase>({ phase: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // The row awaiting confirmation, held as a snapshot rather than an id: the
  // dialog names the caller it is about, and only the id is ever written.
  const [confirmRow, setConfirmRow] = useState<MissedCallResponse | null>(null);

  // Which page the NEXT request asks for. A ref rather than state, because
  // nothing renders from it (the pager reads the page that actually loaded)
  // and because a failed page change has to CORRECT it without thereby firing
  // another request. Every request is driven by `attempt` alone, so asking for
  // the same page twice always re-requests it.
  const requestedPage = useRef(0);

  // The panel's real single-flight lock, one ref per operation. The rendered
  // controls disable themselves from `refreshing` and `pendingId`, but that
  // state is committed a render (and, for the refresh, a passive effect)
  // AFTER the operation starts, and both a queued click and a timer tick can
  // land inside that gap holding a view of the panel that is one render old.
  // Refs are the only thing every entry point can read and write
  // synchronously, so they are what actually enforces one operation at a
  // time; the disabled attributes are what make it visible.
  //
  // `loadInFlight` begins true because mounting starts a load.
  const loadInFlight = useRef(true);
  const writeInFlight = useRef(false);
  const busy = () => loadInFlight.current || writeInFlight.current;

  // An open confirmation is not an operation, but it holds the automatic
  // refresh off for the same reason the others do: the dialog names one
  // caller, and the queue must not be re-read, reordered, or emptied under a
  // question the pharmacist is in the middle of answering. A ref, again,
  // because the timer's view of the panel is a render old.
  const confirmOpen = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    const pageNum = requestedPage.current;

    // The page whose rows are on screen as this load starts. `requestedPage`
    // has already moved when a page change is what fails, so this is what the
    // pager must fall back to.
    const loadedPageNum = state.phase === "loaded" ? state.page.pageNum : null;

    setState((prev) =>
      prev.phase === "loaded"
        ? { ...prev, refreshing: true, refreshError: null }
        : { phase: "loading" }
    );

    getMissedCalls({ pageNum }, controller.signal).then(
      (page) => {
        if (controller.signal.aborted) return;
        // The page number is this panel's own, and its own 404/409
        // reconciliation is what shrinks the set underneath it. Correcting a
        // stale client cursor is not compensating for the server, which
        // faithfully returns the page it was asked for. Ask for the page that
        // now exists instead of rendering an empty one. Both corrections
        // leave `loadInFlight` set: they request again immediately, so the
        // panel is never idle between the two GETs.
        if (page.totalNumOfPages > 0 && pageNum >= page.totalNumOfPages) {
          requestedPage.current = page.totalNumOfPages - 1;
          setAttempt((n) => n + 1);
          return;
        }
        if (page.totalNumOfPages === 0 && pageNum !== 0) {
          requestedPage.current = 0;
          setAttempt((n) => n + 1);
          return;
        }
        loadInFlight.current = false;
        setState({
          phase: "loaded",
          page,
          refreshing: false,
          refreshError: null,
        });
      },
      () => {
        // Cancellation only ever comes from this cleanup, so a CanceledError
        // always arrives aborted and never reaches an error state.
        if (controller.signal.aborted) return;
        loadInFlight.current = false;
        if (loadedPageNum == null) {
          setState({ phase: "error" });
          return;
        }
        // A failed page change must not leave the next request, or a retry,
        // aimed at a page whose rows were never loaded. This corrects the
        // target only: it deliberately fires nothing.
        requestedPage.current = loadedPageNum;
        setState((prev) =>
          prev.phase === "loaded"
            ? {
                ...prev,
                refreshing: false,
                refreshError: MISSED_CALLS_REFRESH_ERROR,
              }
            : { phase: "error" }
        );
      }
    );

    return () => controller.abort();
    // `state` is read only to capture the page on screen when this load starts,
    // which is precisely the value a mid-flight state change must not replace.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const reload = () => {
    loadInFlight.current = true;
    setAttempt((n) => n + 1);
  };

  /** An explicit refresh, and a page change, both retire the previous message. */
  const refresh = () => {
    if (busy()) return;
    setActionError(null);
    reload();
  };

  const changePage = (page: number) => {
    if (busy()) return;
    setActionError(null);
    requestedPage.current = page - 1;
    reload();
  };

  // The automatic refresh is deliberately the ORDINARY loaded refresh and
  // nothing else: the rows on screen survive it, a failure lands in
  // `refreshError` instead of replacing the panel, and it never retires an
  // action message. D10 clears those on user intent (a page change, an
  // explicit refresh, another write), and a timer is not intent: a message
  // the pharmacist has not read yet must not be swept away underneath them.
  const autoRefresh = () => {
    // A first load has nothing to refresh, and a first-load failure keeps its
    // own retry; polling that state would replace the error and its button
    // with "Učitavanje..." every interval.
    if (state.phase !== "loaded") return;
    // Single flight, across GETs and the write alike, read from the refs
    // rather than from this closure: the closure is refreshed in an effect,
    // so a click committed moments ago is not in it yet. A write's own
    // reconciliation stays exactly one GET.
    if (busy() || confirmOpen.current) return;
    reload();
  };

  // The timer is mounted once and must not restart whenever the panel
  // re-renders, so it calls through the latest closure rather than depending
  // on it. Restarting on every state change would also reset the countdown
  // often enough that a busy panel might never reach a full interval.
  const latestAutoRefresh = useRef(autoRefresh);
  useEffect(() => {
    latestAutoRefresh.current = autoRefresh;
  });

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;

    const stop = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };

    const start = () => {
      stop();
      timer = setInterval(
        () => latestAutoRefresh.current(),
        MISSED_CALLS_POLL_MS
      );
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        stop();
        return;
      }
      // Coming back to the tab is exactly when the list is most likely to be
      // stale, so it re-reads now, and the next tick is a full interval from
      // this moment rather than from whenever the tab was hidden.
      latestAutoRefresh.current();
      start();
    };

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  // Marking a call contacted is a one-way statement about a patient, made
  // from a row in a list that can move under the pharmacist, so it is asked
  // before it is written.
  //
  // Opening the dialog is deliberately NOT gated on `busy()`: a swallowed
  // click on a row would leave the pharmacist looking at a button that did
  // nothing. If a refresh is in flight, the dialog opens and its confirm is
  // the control that waits, visibly.
  //
  // Opening it retires no message: D10 clears those when a write STARTS, and
  // a question the pharmacist may still answer with "Odustani" has started
  // nothing.
  const openConfirm = (row: MissedCallResponse) => {
    confirmOpen.current = true;
    setConfirmRow(row);
  };

  const closeConfirm = () => {
    confirmOpen.current = false;
    setConfirmRow(null);
  };

  const confirmContacted = () => {
    const id = confirmRow?.id;
    closeConfirm();
    if (id != null) void markContacted(id);
  };

  const markContacted = async (id: number) => {
    // One operation at a time across the panel, writes and loads alike: a
    // click the browser had already queued when an automatic refresh started
    // arrives at a button that is still live, and must not put a PUT
    // alongside that GET.
    if (busy()) return;
    writeInFlight.current = true;
    setPendingId(id);
    setActionError(null);
    try {
      await markMissedCallContacted(id);
      // The contact time is server-generated, so the row is re-read rather
      // than guessed at.
      reload();
    } catch (error) {
      setActionError(mapContactError(error));
      if (shouldRefetchAfterContactError(error)) {
        // 404 and 409 both prove the row on screen is stale.
        reload();
      }
    } finally {
      // Never an unlocked instant: on every path that reconciles, `reload()`
      // has already taken `loadInFlight` above, so the panel hands the lock
      // straight from the write to its re-read.
      writeInFlight.current = false;
      setPendingId(null);
    }
  };

  const renderContact = (value: string | null, scheme: "mailto" | "tel") => (
    <span className={styles.contactLine}>
      {value ? (
        <a className={styles.contactLink} href={`${scheme}:${value}`}>
          {value}
        </a>
      ) : (
        "-"
      )}
    </span>
  );

  const renderRow = (row: MissedCallResponse, refreshing: boolean) => (
    <tr key={row.id}>
      <td className={styles.td}>
        {formatCallerName(row.firstName, row.secondName)}
      </td>
      <td className={styles.td}>
        {renderContact(row.email, "mailto")}
        {renderContact(row.phoneNumber, "tel")}
      </td>
      <td className={`${styles.td} ${styles.nowrap}`}>
        {formatDateTime(row.callDateTime)}
      </td>
      <td className={`${styles.td} ${styles.nowrap}`}>
        {formatWaitTime(row.waitTimeSeconds)}
      </td>
      {/* The action sits with the status it changes, deliberately. Its own
          column could not explain itself: an action column conventionally
          carries no header, and its cells are empty for every call that has
          been returned, so a page where the queue had been worked through
          showed a narrow blank stripe under no heading at all. Here the badge
          above the button says what the button is for, and such a page simply
          has one column fewer. */}
      <td className={styles.td}>
        <span
          className={
            row.contacted
              ? `${styles.statusBadge} ${styles.statusBadgeDone}`
              : styles.statusBadge
          }
        >
          {row.contacted ? "Kontaktiran" : "Nije kontaktiran"}
        </span>
        {row.contacted && row.contactedUtc && (
          <span className={styles.muted}>
            {formatDateTime(row.contactedUtc)}
          </span>
        )}
        {!row.contacted && (
          <Button
            type="button"
            buttonType="blue"
            className={styles.rowAction}
            disabled={pendingId != null || refreshing}
            onClick={() => openConfirm(row)}
          >
            {pendingId === row.id ? "Čuvanje..." : "Označi kao kontaktiran"}
          </Button>
        )}
      </td>
    </tr>
  );

  const renderTable = (page: PageResponse<MissedCallResponse>, refreshing: boolean) => (
    <>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th} scope="col">
                Korisnik
              </th>
              <th className={styles.th} scope="col">
                Kontakt
              </th>
              <th className={styles.th} scope="col">
                Datum i vreme poziva
              </th>
              <th className={styles.th} scope="col">
                Vreme čekanja
              </th>
              <th className={styles.th} scope="col">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {page.elements.map((row) => renderRow(row, refreshing))}
          </tbody>
        </table>
      </div>
      {page.totalNumOfPages > 1 && (
        <div className={styles.pager}>
          <Pagination
            total={page.totalNumOfElements}
            limit={MISSED_CALLS_PAGE_SIZE}
            activePage={page.pageNum + 1}
            onChangePage={changePage}
            // A write that settles after a page change would drop a message
            // about a row on page 1 on top of page 2, the outliving D10
            // forbids. Navigation closes while a write, or the reload it
            // triggers, is in flight.
            //
            // `undefined` when idle, never `false`: rsuite reads ANY defined
            // value as a total override of its own per-button rules, so a
            // plain `false` would re-enable "Previous" on page 1 (eventKey 0,
            // a request for page -1), "Next" on the last page, and the
            // ellipsis, whose eventKey is the string "ellipsis".
            disabled={refreshing || pendingId != null ? true : undefined}
            prev
            next
            maxButtons={7}
            ellipsis
            boundaryLinks
          />
        </div>
      )}
    </>
  );

  const renderBody = () => {
    if (state.phase === "loading") {
      return (
        <p className={styles.hint}>Učitavanje propuštenih poziva...</p>
      );
    }
    if (state.phase === "error") {
      return (
        <>
          <p className={styles.error} role="alert">
            {MISSED_CALLS_LOAD_ERROR}
          </p>
          {/* Deliberately the unguarded primitive. A retry is an explicit
              demand from somebody looking at an error, and a second one
              replaces the first rather than joining it: the load effect
              aborts the request in flight before issuing the next. */}
          <Button type="button" buttonType="white" onClick={reload}>
            Pokušaj ponovo
          </Button>
        </>
      );
    }

    const { page, refreshing, refreshError } = state;
    return (
      <>
        {/* Both alerts live OUTSIDE the empty-versus-table branch on purpose:
            the row that 404s can be the last missed call, and a message
            explaining why the list just emptied must not vanish with it. */}
        {actionError && (
          <p className={styles.error} role="alert">
            {actionError}
          </p>
        )}
        {refreshError && (
          <p className={styles.error} role="alert">
            {refreshError}
          </p>
        )}
        {page.elements.length === 0 ? (
          <p className={styles.hint}>Nema propuštenih poziva.</p>
        ) : (
          renderTable(page, refreshing)
        )}
      </>
    );
  };

  return (
    <section
      className={className ? `${styles.card} ${className}` : styles.card}
      aria-labelledby={headingId}
    >
      <div className={styles.panelHeader}>
        <h2 id={headingId} className={styles.cardTitle}>
          Propušteni pozivi
        </h2>
        {state.phase === "loaded" && (
          <div className={styles.panelHeaderActions}>
            <span className={styles.hint}>
              Ukupno: {state.page.totalNumOfElements}
            </span>
            <Button
              type="button"
              buttonType="white"
              disabled={state.refreshing || pendingId != null}
              onClick={refresh}
            >
              Osveži
            </Button>
          </div>
        )}
      </div>
      {renderBody()}
      <AccessibleDialog
        isOpen={confirmRow != null}
        onRequestClose={closeConfirm}
        titleId={confirmTitleId}
        maxWidth={480}
      >
        {confirmRow && (
          <div className={styles.dialogBody}>
            <h3 id={confirmTitleId} className={styles.dialogTitle}>
              Da li želite da označite da je korisnik kontaktiran?
            </h3>
            {/* Which call, by name and by time: the queue is shared and its
                rows move, so the dialog says what it is about rather than
                relying on the row the pharmacist happened to aim at. */}
            <p className={styles.hint}>
              {formatCallerName(confirmRow.firstName, confirmRow.secondName)},
              poziv {formatDateTime(confirmRow.callDateTime)}
            </p>
            <div className={styles.dialogActions}>
              <Button type="button" buttonType="white" onClick={closeConfirm}>
                Odustani
              </Button>
              <Button
                type="button"
                buttonType="blue"
                // The panel can be mid-refresh when the dialog opens, and a
                // confirm the write lock would swallow must not look like it
                // was accepted. It waits, visibly, and then works.
                disabled={
                  pendingId != null ||
                  (state.phase === "loaded" && state.refreshing)
                }
                onClick={confirmContacted}
              >
                Potvrdi
              </Button>
            </div>
          </div>
        )}
      </AccessibleDialog>
    </section>
  );
};

export default MissedCallsPanel;
